import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Quote, Send, Sparkles, CheckCircle2, Clock, X } from "lucide-react";

export default function TestimonialDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({ quote: "", stat: "", stat_label: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/me/testimonial").then((r) => {
      const data = r.data || {};
      setExisting(data && data.id ? data : null);
      if (data && data.id) {
        setForm({ quote: data.quote || "", stat: data.stat || "", stat_label: data.stat_label || "" });
      }
    }).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/testimonials", form);
      toast.success("Témoignage envoyé. Il sera publié après modération.");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <Quote className="h-5 w-5 text-[#002FA7]" /> Laisser un témoignage
          </DialogTitle>
        </DialogHeader>

        {existing ? (
          <div className="space-y-3" data-testid="existing-testimonial">
            <div className={`p-4 border ${existing.status === "approved" ? "border-[#059669] bg-[#ECFDF5]" : existing.status === "pending" ? "border-[#FCD34D] bg-[#FFFBEB]" : "border-[#FCA5A5] bg-[#FEF2F2]"}`}>
              <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] font-bold">
                {existing.status === "approved" && <><CheckCircle2 className="h-4 w-4 text-[#059669]" /> Publié</>}
                {existing.status === "pending" && <><Clock className="h-4 w-4 text-[#92400E]" /> En attente de modération</>}
                {existing.status === "rejected" && <><X className="h-4 w-4 text-[#991B1B]" /> Refusé</>}
              </div>
              <div className="text-sm italic">« {existing.quote} »</div>
              {existing.stat && (
                <div className="mt-2 text-xs"><b>{existing.stat}</b> {existing.stat_label}</div>
              )}
            </div>
            <p className="text-xs text-[#495057]">
              Vous pouvez soumettre un nouveau témoignage qui remplacera le précédent (il sera de nouveau modéré).
            </p>
            <Button onClick={() => setExisting(null)} variant="outline" className="w-full" data-testid="testimonial-replace">
              <Sparkles className="h-4 w-4 mr-2" /> Soumettre un nouveau témoignage
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="testimonial-form">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Votre citation</Label>
              <Textarea
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                rows={4}
                maxLength={300}
                placeholder="Partagez votre expérience avec PAG en quelques phrases…"
                required
                data-testid="testimonial-quote"
              />
              <div className="text-[10px] text-[#868E96] text-right">{form.quote.length}/300</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Chiffre clé</Label>
                <Input
                  value={form.stat}
                  onChange={(e) => setForm({ ...form, stat: e.target.value })}
                  placeholder="+12h"
                  data-testid="testimonial-stat"
                  className="h-10"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Légende du chiffre</Label>
                <Input
                  value={form.stat_label}
                  onChange={(e) => setForm({ ...form, stat_label: e.target.value })}
                  placeholder="gagnées / mois"
                  data-testid="testimonial-stat-label"
                  className="h-10"
                />
              </div>
            </div>
            <div className="p-3 border border-[#DEE2E6] bg-[#F8F9FA] text-xs text-[#495057]">
              Votre nom, votre rôle et votre entreprise seront affichés. Le témoignage sera publié après validation par l'administrateur PAG.
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting || !form.quote.trim()} data-testid="testimonial-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
                <Send className="h-4 w-4 mr-2" /> {submitting ? "Envoi…" : "Envoyer mon témoignage"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
